import React, { useState, useEffect, createContext, useContext } from 'react';
import { UserContext } from '../UserContext';
import { useLocation, useParams } from 'react-router-dom';
import {
	generateConfig,
	flattenObject,
	getJsonDiff,
	patchJson,
	preProcessConfigAfterFlatten,
	preProcessConfigBeforeDeFlatten
} from '../../utilities/helpers';
import { getAllSiteTags, getSiteConfig, logPullAndMerge } from '../../api/sites';
import { useNavigate } from 'react-router-dom';
import {
	initialAggressiveVpMuteCoverageConfig,
	initialCustomParentToChildMapping,
	initialPrebidObj,
	initialSectionLevelSplitConfig,
	initialTpVastReportingConfig,
	initialTransformedGamIds
} from '../../utilities/initialConfigs';
import { getTgmrDoc } from '../../api/sites';
import { toast } from 'react-toastify';
import useSplitDoc from '../../CustomHooks/useSplitDoc';
import useExpbDoc from '../../CustomHooks/useExpbDoc';
import useTpVastDoc from '../../CustomHooks/useTpVastDoc';
import usePlaylistMapping from '../../CustomHooks/usePlaylistMapping';
import useDfpNetworkConfig from '../../CustomHooks/useDfpNetworkConfig';
import { v4 as uuid } from 'uuid';
import { CONFIG_EDIT_MODES, PULL_AND_MERGE_STATUS } from '../../core/constants';

export const UnitsContext = createContext();
//units === videoSections
//UnitsContext is the global context for this application , it maintains which videoSection Unit is selected at this moment and more
const UnitsProvider = ({ children }) => {
	const { user } = useContext(UserContext);
	const navigate = useNavigate();
	const [units, setUnits] = useState({}); // contains all videoSection Units as key,value pair
	const [curSelectedUnit, setCurSelectedUnit] = useState(''); // sectionRef of the currently selected videoSection
	const [nonArchivedUnitsArray, setNonArchivedUnitsArray] = useState([]); // Contains Non-Archived Video Sections Ids
	const [customParentToChildMappingConfig, setCustomParentToChildMappingConfig] = useState(
		initialCustomParentToChildMapping
	);
	const [aggressiveVpMuteCoverageConfig, setAggressiveVpMuteCoverageConfig] = useState(
		initialAggressiveVpMuteCoverageConfig
	);
	const [siteRawConfig, setSiteRawConfig] = useState({
		siteId: '',
		siteDomain: '',
		mcmId: '',
		gamId: '103512698',
		videoPlayerId: 'ap-player',
		dateCreated: '',
		tag: '',
		ownerEmail: ''
	});
	const [sectionLevelSplit, setSectionLevelSplitConfig] = useState(initialSectionLevelSplitConfig);
	const [transformedGamIdsConfig, setTransformedGamIdsConfig] = useState(initialTransformedGamIds);
	const [tgmrDoc, setTgmrDoc] = useState();
	const [prebidRawConfig, setPrebidRawConfig] = useState({ prebid: initialPrebidObj }); //contains the prebid data as {prebid:{}}
	const [mode, setMode] = useState(''); //options are "CREATE" | "UPDATE" | "SYNC" | "UPDATE_SUPER_ADMIN"
	const [previousCouchBaseConfig, setPreviousCouchBaseConfig] = useState(null);
	const [tpVastRawConfig, setTpVastRawConfig] = useState(initialTpVastReportingConfig);
	const [allValidSiteTags, setAllValidSiteTags] = useState([]);
	const [framerateNetworkConfig, setFramerateNetworkConfig] = useState(null);
	const location = useLocation();
	const { siteId } = useParams();
	const {
		splitDoc,
		splitDocEditMode,
		updateSplitDoc,
		getSplitDocWithEncodedScripts,
		previousSplitDoc,
		updateSplitDocEditMode,
		getSplitDocWithDecodedScripts
	} = useSplitDoc(siteId);

	const {
		expbDoc,
		updateExpbDoc,
		expbDocEditMode,
		userHasExpbDocEditAccess,
		previousExpbDoc,
		allAccessEmails,
		updateExpbEditMode
	} = useExpbDoc(siteId, user);
	const {
		tpVastDoc,
		updateTpVastDoc,
		selectedTpValues,
		setSelectedTpValues,
		updateSelectedTpValues,
		tpVastDocEditMode,
		setTpVastDocEditMode,
		previousTpVastDoc
	} = useTpVastDoc(siteId);

	const playlistMappingStatesAndFunctions = usePlaylistMapping(siteRawConfig.ownerEmail);
	const { dfpNetworkConfig } = useDfpNetworkConfig();

	//useEffects==========================
	//update mode
	useEffect(() => {
		if (!user) navigate('/login');
		if (location.pathname === '/create-config') setMode('CREATE');
		else if (location.pathname.includes('update-config')) setMode('UPDATE');
	});

	//Fetches and updates initial Site config data when mode loads ( mode are "CREATE" or "UPDATE" )
	useEffect(() => {
		const fetchSiteTagsAndConfig = async () => {
			try {
				const validSiteTags = await getAllSiteTags();
				setAllValidSiteTags(validSiteTags.data.data.tags.validTags);

				if (mode === 'UPDATE') {
					if (!siteId) {
						toast.error('Site Id is missing', {
							position: toast.POSITION.BOTTOM_RIGHT
						});
						return;
					}

					const { data } = await getSiteConfig(siteId);
					const siteObject = structuredClone(data?.data?.site);
					initializeConfigData(data?.data?.site);
					setPreviousCouchBaseConfig(siteObject);
				}
			} catch (err) {
				console.log('UnitsConfig logging : ', err);
			}
		};

		if (mode === 'CREATE' || mode === 'UPDATE') {
			fetchSiteTagsAndConfig();
		}
	}, [mode]);

	async function updateTgmrDoc() {
		try {
			if (!siteId) {
				return;
			}
			const { data } = await getTgmrDoc(siteId);

			let tgmrObj = data?.data?.tgmrDoc;

			if (!tgmrObj) {
				throw new Error(`Ad config is not present for this site, pls create the apTags`);
			}

			setTgmrDoc(tgmrObj);
		} catch (err) {
			toast.error('Ad config not found, Create apTags for the site from the Console', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			console.error('Tgmr Doc fetch error : ', err);
		}
	}

	//functions===========================
	const updateUnit = (sectionRef, dataOrCallback) => {
		console.log('Unit updated (SITE LEVEL) : ', sectionRef);
		setUnits((prevUnits) => {
			const nextUnitData =
				typeof dataOrCallback === 'function'
					? dataOrCallback(prevUnits?.[sectionRef] || {})
					: dataOrCallback;

			return { ...prevUnits, [sectionRef]: nextUnitData };
		});
	};

	const updateUnits = (valueOrCallback) => {
		if (typeof valueOrCallback === 'function') {
			setUnits((prevUnits) => valueOrCallback(prevUnits));
			return;
		}

		setUnits(valueOrCallback);
	};

	const addMissingTPVastConfig = (tpVastConfiguration) => {
		// check for all supported tpvast demands and add config if any demand config is missing in the current tpVastConfiguration
		const demands = Object.keys(initialTpVastReportingConfig.demandConfiguration);
		demands.forEach((demand) => {
			if (!tpVastConfiguration.demandConfiguration[demand]) {
				tpVastConfiguration.demandConfiguration[demand] =
					initialTpVastReportingConfig.demandConfiguration[demand];
			}
		});

		return tpVastConfiguration;
	};

	//this function updates initial Data when mode is 'UPDATE'
	const initializeConfigData = (data, initialSelectedVideoSectionRef) => {
		let {
			siteId,
			mcmId,
			siteDomain,
			gamId,
			tpVastConfiguration,
			prebid,
			ads,
			videoPlayerId,
			dateCreated,
			tag,
			sectionLevelSplit,
			customParentToChildMapping,
			aggressiveVpMuteCoverageConfig,
			connectedGamIds,
			...additionalConfigData
		} = data;

		/* additionalConfigData contains the configs which are not handled by dashboard */

		if (!gamId) gamId = '103512698';
		setSiteRawConfig({
			siteId: parseInt(siteId),
			mcmId,
			gamId,
			siteDomain,
			tag,
			videoPlayerId,
			dateCreated,
			...additionalConfigData
		});
		setPrebidRawConfig({ prebid });
		sectionLevelSplit = sectionLevelSplit ? sectionLevelSplit : initialSectionLevelSplitConfig;
		setSectionLevelSplitConfig(sectionLevelSplit);
		tpVastConfiguration = tpVastConfiguration ? tpVastConfiguration : initialTpVastReportingConfig;
		// check and add vidgyor config if missing in tpVastConfiguration to avoid errors in tpVastConfig component
		tpVastConfiguration = addMissingTPVastConfig(tpVastConfiguration);
		setTpVastRawConfig(tpVastConfiguration);

		customParentToChildMapping = customParentToChildMapping
			? customParentToChildMapping
			: initialCustomParentToChildMapping;

		const finalAggressiveVpMuteCoverageConfig =
			aggressiveVpMuteCoverageConfig ?? initialAggressiveVpMuteCoverageConfig;

		setCustomParentToChildMappingConfig(customParentToChildMapping);
		setAggressiveVpMuteCoverageConfig(finalAggressiveVpMuteCoverageConfig);
		let transformedGamId =
			connectedGamIds &&
			connectedGamIds.map((gamId) => {
				return { value: gamId, editable: false };
			});
		connectedGamIds = connectedGamIds ? transformedGamId : initialTransformedGamIds;
		setTransformedGamIdsConfig(connectedGamIds);

		const adsObj = {};
		let filteredUnitsArray = [];

		ads.forEach((ad) => {
			// Pushes Non-Archived Section Ids if user not superAdmin else pushes all Sections
			if (!ad.isArchived || user.isSuperAdmin) {
				// Adds a new sectionRef Property to videoSection, if not already present.
				if (!ad.sectionRef) {
					ad.sectionRef = uuid().toString();
				}
				adsObj[ad.sectionRef] = flattenObject(ad);
				adsObj[ad.sectionRef] = preProcessConfigAfterFlatten(adsObj[ad.sectionRef]);
				filteredUnitsArray.push(ad.sectionRef);
			}
		});

		setUnits(adsObj);
		setNonArchivedUnitsArray(filteredUnitsArray);

		/**
		 * Sets the First Video Section Ref from Filtered Units Array, If initialSelectedVideoSectionRef is not provided!
		 */
		if (filteredUnitsArray.length > 0) {
			const selectedVideoSection = initialSelectedVideoSectionRef
				? initialSelectedVideoSectionRef
				: filteredUnitsArray[0];
			setCurSelectedUnit(selectedVideoSection);
		} else {
			toast.error('All Video Sections are Archived!', { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	const getFinalOverviewObj = () => {
		let connectedGamIds = transformedGamIdsConfig.map((gamObject) => {
			return gamObject.value;
		});
		const obj = {
			...siteRawConfig,
			prebid: prebidRawConfig?.prebid,
			customParentToChildMapping: customParentToChildMappingConfig,
			aggressiveVpMuteCoverageConfig,
			tpVastConfiguration: tpVastRawConfig,
			sectionLevelSplit,
			connectedGamIds
		};
		obj.ads = [];
		for (let sectionRef in units) {
			const framerateDocWithEncodedConfig = preProcessConfigBeforeDeFlatten(units[sectionRef]);
			obj.ads.push(generateConfig(framerateDocWithEncodedConfig));
		}
		return obj;
	};

	const updateSelectedUnit = (currSectionRef) => {
		setCurSelectedUnit(currSectionRef);
	};

	/**
	 * @name handlePullAndMergeSiteConfig
	 * @description Pulls the Site Config from the Couchbase and Merges it with the current Site Config
	 * This function is called when the user wants to update the site config with the latest changes
	 */
	const handlePullAndMergeSiteConfig = async () => {
		if (mode !== CONFIG_EDIT_MODES.UPDATE) {
			toast.error('Only can pull and merge if mode is Update!', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			return;
		}
		const currentLocalSiteConfig = getFinalOverviewObj();
		try {
			const currentLocalDiff = getJsonDiff(previousCouchBaseConfig, currentLocalSiteConfig);
			const { data } = await getSiteConfig(siteId);
			const latestSiteConfigFromCb = data?.data?.site;
			if (latestSiteConfigFromCb.cas === currentLocalSiteConfig.cas) {
				toast.info('No new Site changes available!', {
					position: toast.POSITION.BOTTOM_RIGHT
				});
				return;
			}
			let updatedSiteConfig;
			if (Object.keys(currentLocalDiff).length === 0) {
				updatedSiteConfig = latestSiteConfigFromCb;
			} else {
				updatedSiteConfig = patchJson(structuredClone(latestSiteConfigFromCb), currentLocalDiff);
			}
			initializeConfigData(updatedSiteConfig, curSelectedUnit);
			setPreviousCouchBaseConfig(latestSiteConfigFromCb);
			toast.success(
				'Latest Site Changes Pulled and merged! Please check your changes thoroughly before Updating Site!!',
				{
					position: toast.POSITION.BOTTOM_RIGHT
				}
			);

			//This logging is done only for analysis purposes, should be removed once analysis is done
			await logPullAndMerge(siteId, currentLocalSiteConfig, PULL_AND_MERGE_STATUS.SUCCESS);
		} catch (err) {
			toast.error('Error in Merging Latest Site Config', { position: toast.POSITION.BOTTOM_RIGHT });
			console.log(err);

			//This logging is done only for analysis purposes, should be removed once analysis is done
			await logPullAndMerge(siteId, currentLocalSiteConfig, PULL_AND_MERGE_STATUS.FAILED);
		}
	};

	return (
		<UnitsContext.Provider
			value={{
				initializeConfigData,
				mode,
				units,
				updateUnits,
				allValidSiteTags,
				curSelectedUnit,
				updateUnit,
				framerateNetworkConfig,
				updateFramerateNetworkConfig: setFramerateNetworkConfig,
				updateSelectedUnit,
				handlePullAndMergeSiteConfig,
				siteRawConfig,
				updateSiteRawConfig: setSiteRawConfig,
				prebidRawConfig,
				updatePrebidRawConfig: setPrebidRawConfig,
				tpVastRawConfig,
				updateTpVastRawConfig: setTpVastRawConfig,
				previousCouchBaseConfig,
				setPreviousCouchBaseConfig,
				getFinalOverviewObj,
				sectionLevelSplit,
				updateSectionLevelSplit: setSectionLevelSplitConfig,
				transformedGamIdsConfig,
				updateTransformedGamIds: setTransformedGamIdsConfig,
				customParentToChildMappingConfig,
				updateCustomParentToChildMappingConfig: setCustomParentToChildMappingConfig,
				aggressiveVpMuteCoverageConfig,
				updateAggressiveVpMuteCoverageConfig: setAggressiveVpMuteCoverageConfig,
				tgmrDoc,
				updateTgmrDoc,
				splitDoc,
				splitDocEditMode,
				updateSplitDocEditMode,
				updateSplitDoc,
				getSplitDocWithEncodedScripts,
				getSplitDocWithDecodedScripts,
				previousSplitDoc,
				expbDoc,
				updateExpbDoc,
				expbDocEditMode,
				userHasExpbDocEditAccess,
				previousExpbDoc,
				allAccessEmails,
				updateExpbEditMode,
				tpVastDoc,
				updateTpVastDoc,
				selectedTpValues,
				setSelectedTpValues,
				updateSelectedTpValues,
				tpVastDocEditMode,
				setTpVastDocEditMode,
				previousTpVastDoc,
				nonArchivedUnitsArray,
				dfpNetworkConfig,
				updateNonArchivedUnitsArray: setNonArchivedUnitsArray,
				...playlistMappingStatesAndFunctions
			}}
		>
			{children}
		</UnitsContext.Provider>
	);
};

export default UnitsProvider;
